import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, AlertTriangle, Settings } from 'lucide-react';

const SSOStatusCard = ({ ssoStatus, organizationId }) => {
  const navigate = useNavigate();

  const getProviderIcon = (provider) => {
    const icons = {
      okta: '🔷',
      azure_ad: '🔵',
      google_workspace: '🔴'
    };
    return icons?.[provider] || '🔒';
  };

  const getProviderName = (provider) => {
    const names = {
      okta: 'Okta',
      azure_ad: 'Azure AD',
      google_workspace: 'Google Workspace'
    };
    return names?.[provider] || provider;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'testing':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <div className="w-5 h-5 rounded-full bg-gray-300" />;
    }
  };

  const activeProviders = ssoStatus?.filter(s => s?.connectionStatus === 'active') || [];
  const totalAttempts = ssoStatus?.reduce((sum, s) => sum + (s?.totalAttempts || 0), 0);
  const avgSuccessRate = ssoStatus?.length > 0
    ? ssoStatus?.reduce((sum, s) => sum + (s?.authSuccessRate || 0), 0) / ssoStatus?.length
    : 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">SSO Status</h3>
            <p className="text-sm text-gray-600">Enterprise authentication</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/sso-settings')}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Configure SSO"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {ssoStatus?.length === 0 ? (
        <div className="text-center py-8">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No SSO providers configured</p>
          <button
            onClick={() => navigate('/sso-settings')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
          >
            Configure SSO
          </button>
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{activeProviders?.length}</div>
              <div className="text-xs text-gray-600">Active Providers</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{avgSuccessRate?.toFixed(0)}%</div>
              <div className="text-xs text-gray-600">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{totalAttempts}</div>
              <div className="text-xs text-gray-600">Total Logins</div>
            </div>
          </div>

          {/* Provider List */}
          <div className="space-y-3">
            {ssoStatus?.map(status => (
              <div
                key={status?.provider}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getProviderIcon(status?.provider)}</span>
                  <div>
                    <div className="font-medium text-gray-900">
                      {getProviderName(status?.provider)}
                    </div>
                    <div className="text-xs text-gray-600">
                      {status?.totalAttempts} attempts • {status?.authSuccessRate?.toFixed(0)}% success
                    </div>
                  </div>
                </div>
                {getStatusIcon(status?.connectionStatus)}
              </div>
            ))}
          </div>

          {/* View Details Link */}
          <button
            onClick={() => navigate('/sso-settings')}
            className="w-full mt-4 text-center text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            View detailed SSO settings →
          </button>
        </>
      )}
    </div>
  );
};

export default SSOStatusCard;